import { Language } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { organizationService } from '../../application/OrganizationService';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseAcceptOrganizationUserInvitationProps {
  token: string;
  password: string;
  language: Language;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

type UseAcceptOrganizationUserInvitationResponse = ServiceResponse<
  ({ token, password, firstName, lastName, phone }: UseAcceptOrganizationUserInvitationProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useAcceptOrganizationUserInvitation = (): UseAcceptOrganizationUserInvitationResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({
      token,
      password,
      language,
      firstName,
      lastName,
      phone,
    }: UseAcceptOrganizationUserInvitationProps): Promise<void> => {
      return organizationService.acceptUserInvitation({
        token,
        password,
        language,
        firstName,
        lastName,
        phone,
      });
    },
    {
      onError: (err: any) => {
        console.info(`Accept Organization User Invitation Error:`, err);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.accept_invitation_fail`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: ({ token, password, language, firstName, lastName, phone }: UseAcceptOrganizationUserInvitationProps) =>
      mutate({ token, password, language, firstName, lastName, phone }),
  };
};
